import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listKafCreations, saveKafCreation, deleteKafCreation } from "@/lib/kaf-academy.functions";
import { LivingBackdrop } from "@/academy/engines/living/LivingBackdrop";
import { Trash2, Plus } from "lucide-react";
import { useState } from "react";

const KINDS: { key: string; label: string }[] = [
  { key: "writing", label: "Writing" },
  { key: "music",   label: "Music" },
  { key: "visual",  label: "Visual" },
  { key: "project", label: "Project" },
];

export const Route = createFileRoute("/_authenticated/academy/kaf/studio")({
  head: () => ({ meta: [{ title: "Creative Studio — KAfrequency" }] }),
  component: StudioPage,
});

function StudioPage() {
  const qc = useQueryClient();
  const listFn = useServerFn(listKafCreations);
  const saveFn = useServerFn(saveKafCreation);
  const delFn = useServerFn(deleteKafCreation);
  const [kind, setKind] = useState<string>("writing");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [editing, setEditing] = useState<string | null>(null);

  const q = useQuery({ queryKey: ["kaf", "creations"], queryFn: () => listFn() });
  const save = useMutation({
    mutationFn: () => saveFn({ data: { id: editing ?? undefined, kind, title: title || "Untitled", body } }),
    onSuccess: () => {
      setTitle(""); setBody(""); setEditing(null);
      qc.invalidateQueries({ queryKey: ["kaf", "creations"] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => delFn({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["kaf", "creations"] }),
  });

  const filtered = (q.data ?? []).filter((c) => c.kind === kind);

  return (
    <div className="relative min-h-dvh bg-obsidian text-foreground">
      <div className="absolute inset-0 opacity-60"><LivingBackdrop hue={20} density={70} /></div>
      <section className="relative z-10 mx-auto max-w-5xl px-4 py-12 sm:px-6">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Creative Studio</p>
        <h1 className="mt-2 font-serif text-4xl text-gold-luminous">Bring the unseen through</h1>

        <div className="mt-6 flex flex-wrap gap-2">
          {KINDS.map((k) => (
            <button key={k.key} onClick={() => { setKind(k.key); setEditing(null); setTitle(""); setBody(""); }}
              className={"rounded-full px-4 py-1.5 text-xs uppercase tracking-widest transition " +
                (kind === k.key ? "bg-gold-gradient text-obsidian" : "border hairline text-foreground/70 hover:border-gold/60")}>
              {k.label}
            </button>
          ))}
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[1fr,340px]">
          <div className="rounded-2xl border hairline bg-obsidian/70 p-5">
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title"
              className="w-full bg-transparent font-serif text-xl text-gold-luminous outline-none placeholder:text-foreground/40" />
            <textarea value={body} onChange={(e) => setBody(e.target.value)}
              placeholder={kind === "music" ? "Chord notes, lyric fragments, arrangement ideas…" : kind === "visual" ? "Describe the image, palette, references…" : "Draft freely…"}
              className="mt-4 min-h-[280px] w-full bg-transparent text-sm outline-none placeholder:text-foreground/40" />
            <div className="mt-4 flex items-center gap-3">
              <button onClick={() => save.mutate()} disabled={save.isPending}
                className="rounded-full bg-gold-gradient px-5 py-2 text-sm font-semibold text-obsidian disabled:opacity-60">
                <Plus size={14} className="mr-1 inline" />
                {editing ? "Update draft" : "Save draft"}
              </button>
              {editing && (
                <button onClick={() => { setEditing(null); setTitle(""); setBody(""); }} className="text-sm text-foreground/60 hover:text-foreground">
                  New draft
                </button>
              )}
            </div>
          </div>
          <aside className="rounded-2xl border hairline bg-obsidian/70 p-4">
            <div className="text-xs uppercase tracking-widest text-gold">Drafts</div>
            <div className="mt-3 flex flex-col gap-2">
              {filtered.map((c) => (
                <div key={c.id} className="group flex items-start justify-between gap-2 rounded-lg border hairline p-3">
                  <button className="min-w-0 flex-1 text-left" onClick={() => { setEditing(c.id); setTitle(c.title); setBody(c.body); }}>
                    <div className="truncate font-serif text-gold-luminous">{c.title}</div>
                    <div className="text-xs text-foreground/50">{new Date(c.updated_at).toLocaleDateString()}</div>
                  </button>
                  <button onClick={() => remove.mutate(c.id)} className="text-foreground/40 hover:text-red-400" aria-label="Delete">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              {!filtered.length && <p className="text-sm text-foreground/50">No drafts yet.</p>}
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
